import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, StyleSheet, Text, View } from 'react-native';
import { useSharedValue } from 'react-native-reanimated';
import { useDispatch, useSelector } from 'react-redux';

import { getSocket } from '../../../services/socket';
import { RootState } from '../../../store';
import { updateUserLevel } from '../../../store/auth/authActions';
import { roomClose, roomOpen } from '../../../store/room/roomActions';
import { TableItem } from '../../../store/types';
import { socketErrorCode, SvaraErrorCode } from '../../../utils/helpers/svaraErrorCodes';
import { useDevice } from '../../../utils/hooks/useDevice';
import { RootStackParamList } from '../../navigator';
import { CarouselNav } from '../common/CarouselNav';
import { DesktopStripPage } from '../Desktop/DesktopStripPage';
import { TableCard } from '../LobbyTable/TableCard';
import LeveledUpNotificationModal from '../Modals/LeveledUpNotificationModal';
import { RoomTablesCarousel, RoomTablesCarouselHandle } from './RoomTablesCarousel';

type NavigationProp = NativeStackNavigationProp<RootStackParamList, 'Game'>;
export type RoomRoute = RouteProp<RootStackParamList, 'Room'>;

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

const SETTLE_MS = 500;

const NAV_BLOCK_HEIGHT = 34;
const NAV_TOP_GAP = 12;

const DESIGN_FRAME_WIDTH = 390;
const DESIGN_CARD_WIDTH = 226;
const DESIGN_CARD_HEIGHT = 416;
const CARD_ASPECT = DESIGN_CARD_HEIGHT / DESIGN_CARD_WIDTH;

export const RoomTablesSection = () => {
	const { safeAreaWidth: width, safeAreaHeight: height, insets, isXs, isDesktop } = useDevice();
	const { t } = useTranslation();

	const [showLeveledUpNotification, setShowLeveledUpNotification] = useState(false);
	const [newLevel, setNewLevel] = useState(0);
	const [loading, setLoading] = useState(true);
	const loadingRef = useRef(true);
	useEffect(() => {
		loadingRef.current = loading;
	}, [loading]);
	const [activeIndex, setActiveIndex] = useState(0);
	const [dotIndex, setDotIndex] = useState(0);
	const [tablesLocal, setTablesLocal] = useState<TableItem[]>([]);

	const carouselRef = useRef<RoomTablesCarouselHandle>(null);
	const activeIndexRef = useRef(0);
	const carouselProgress = useSharedValue(0);
	const navigation = useNavigation<NavigationProp>();

	const { params } = useRoute<RoomRoute>();
	// Field-level selectors: subscribing to the whole auth slice re-renders this
	// section (and re-registers its socket listeners) on any auth change.
	const userId = useSelector((state: RootState) => state.auth.user?.id);
	const username = useSelector((state: RootState) => state.auth.user?.username);
	const dispatch = useDispatch();
	// VIP tables live on their own screen; an old link or notification into the VIP room goes there.
	const isVipRoom = params?.roomSlug?.trim().toLowerCase() === 'vip';
	useEffect(() => {
		if (isVipRoom) navigation.replace('VipRooms');
	}, [isVipRoom, navigation]);

	const topReserve = height * 0.1 + insets.top;
	const bottomReserve = (isXs ? width * 0.2 : height * 0.18) + insets.bottom;
	const availableHeight = Math.max(height - topReserve - bottomReserve, height * 0.44);
	const navReserveScale = clamp(width / DESIGN_FRAME_WIDTH, 1, 1.6);
	const navHeight = (NAV_BLOCK_HEIGHT + NAV_TOP_GAP) * navReserveScale;
	const sectionHeight = Math.max(availableHeight - navHeight, availableHeight * 0.7);
	const heightBudget = sectionHeight * 0.94;
	const widthBudget = width * (DESIGN_CARD_WIDTH / DESIGN_FRAME_WIDTH);
	const cardWidth = Math.min(widthBudget, heightBudget / CARD_ASPECT);
	const cardHeight = cardWidth * CARD_ASPECT;
	const navScale = cardWidth / DESIGN_CARD_WIDTH;
	const navOffset = NAV_TOP_GAP * navScale - (sectionHeight - cardHeight) / 2;
	const layoutKey = `${Math.round(width)}-${Math.round(sectionHeight)}`;

	const roomIdRef = useRef<string | number | undefined>(params?.roomId);
	useEffect(() => {
		roomIdRef.current = params?.roomId;
	}, [params?.roomId]);

	const roomId = params?.roomId;

	useEffect(() => {
		const socket = getSocket(userId || 'anonymous', username || 'anonymous');
		const roomPayload = { roomId };
		let refetchTimer: ReturnType<typeof setTimeout> | null = null;

		const onConnect = () => {
			socket.emit('service:getTables', roomPayload);
		};

		const onAny = (event: string, payload?: any) => {
			if (
				event === 'joinTablePublisher' ||
				event === 'exitTablePublisher' ||
				event === 'getClosedTables' ||
				event === 'tableCreatedPublisher'
			) {
				// Live seat changes from other players. The publisher payload only names
				// the table (v1 semantics), and the rendered seats come from each table's
				// `seats` array, so refetch the room listing (Redis-backed, already
				// current) rather than guessing which seat changed. Debounced: a seat
				// take/leave burst produces one refetch.
				const eventRoomId = payload?.data?.roomID ?? payload?.roomID;
				if (eventRoomId != null && String(eventRoomId) !== String(roomIdRef.current)) {
					return;
				}
				if (refetchTimer) clearTimeout(refetchTimer);
				refetchTimer = setTimeout(() => {
					refetchTimer = null;
					if (socket.connected) socket.emit('service:getTables', { roomId: roomIdRef.current });
				}, 300);
				return;
			}
			if (event === 'levelUP') {
				const data = payload.data || payload;
				const level = Number(data.level);
				if (!Number.isFinite(level)) return;

				dispatch(updateUserLevel(level));
				setNewLevel(level);
				setShowLeveledUpNotification(true);
				return;
			} else if (event === 'quickSeat') {
				const data = payload?.data || payload || {};

				if (payload?.success && data.tableId) {
					navigation.replace('Game', {
						tableId: data.tableId,
						roomSlug: data.roomSlug,
						isTournamentRoom: false,
					});
				}
				return;
			}
			const maybeTables =
				(payload && Array.isArray(payload.data) && payload.data) ||
				(payload && Array.isArray(payload.items) && payload.items) ||
				(Array.isArray(payload) && payload);

			if (maybeTables.length) {
				const filtered = maybeTables.filter(
					(table: any) => String(table.room_id) === String(roomIdRef.current),
				);
				// Any array payload lands here, including another room's (the VIP room's) table list;
				// only this room's tables are ours to show.
				if (filtered.length) {
					setTablesLocal(filtered);
					setLoading(false);
				}
			}

			if (
				event === 'getTables' &&
				payload?.success === false &&
				socketErrorCode(payload) === SvaraErrorCode.InvalidLevelForTable
			) {
				// The player's level is outside this room's range (typically levelled
				// past Sofia's maxLevel while seated). Not an error to show: the location
				// screen renders the room locked, so go there. Guarded on focus because
				// this screen can also be reached from a stale Back stack or a deep link.
				setLoading(false);
				if (navigation.isFocused()) navigation.replace('Lobby');
				return;
			}

			if (payload && payload.success === false && payload.error) {
				console.warn(`[SOCKET ERROR] ${event}:`, payload.error);
			}
		};

		const onGetTables = (packet: any) => {
			const arr =
				(packet && Array.isArray(packet.data) && packet.data) ||
				(packet && Array.isArray(packet.items) && packet.items) ||
				(Array.isArray(packet) && packet) ||
				[];

			const currentRoomId = roomIdRef.current;

			const filtered = currentRoomId
				? arr.filter((table: any) => String(table.room_id) === String(currentRoomId))
				: arr;
			// The socket is shared: a reply for another room's list is not ours to show.
			if (arr.length > 0 && filtered.length === 0) return;

			setTablesLocal(filtered);
			setLoading(false);
		};

		socket.on('connect', onConnect);

		socket.onAny(onAny);
		socket.on('getTables', onGetTables);

		return () => {
			if (refetchTimer) clearTimeout(refetchTimer);
			socket.off('connect', onConnect);

			socket.offAny(onAny);
			socket.off('getTables', onGetTables);
		};
	}, [userId, username, roomId, dispatch, navigation]);

	useEffect(() => {
		if (!roomId) return;

		const socket = getSocket(userId || 'anonymous', username || 'anonymous');
		const roomPayload = { roomId };

		const emitForRoom = () => {
			socket.emit('service:getTables', roomPayload);
		};

		if (socket.connected) {
			emitForRoom();
		} else {
			socket.once('connect', emitForRoom);
			// A socket that was disconnected server-side never reconnects on its own.
			socket.connect();
		}

		// Watchdog: while the tables are still loading, retry instead of spinning forever
		// (seen 2026-09-03: a duplicate-connection kick left the lobby waiting on a dead
		// socket with REST still working).
		const watchdog = setInterval(() => {
			if (!loadingRef.current) return;
			if (socket.connected) {
				emitForRoom();
			} else {
				socket.connect();
			}
		}, 8000);

		return () => {
			clearInterval(watchdog);
			socket.off('connect', emitForRoom);
		};
	}, [roomId, userId, username]);

	useEffect(() => {
		if (params?.roomId) {
			setLoading(true);
			dispatch(roomOpen({ roomId: String(params.roomId) }));
		}
		// Leaving the screen ends the room saga, whose teardown emits
		// service:leaveLobbyRoom. Nothing dispatched roomClose before, so the worker
		// outlived the screen and its deferred teardown kicked the socket out of the
		// lobby room on the next visit (see roomSocketSaga).
		return () => {
			dispatch(roomClose());
		};
	}, [dispatch, params?.roomId]);

	useEffect(() => {
		activeIndexRef.current = activeIndex;
	}, [activeIndex]);

	useEffect(() => {
		const safeIndex = clamp(activeIndex, 0, Math.max(0, tablesLocal.length - 1));

		if (safeIndex === activeIndex) return;

		activeIndexRef.current = safeIndex;
		setActiveIndex(safeIndex);
		setDotIndex(safeIndex);
		carouselRef.current?.scrollToIndex(safeIndex);
	}, [activeIndex, tablesLocal.length]);

	const commitIndexTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const handleActiveIndexChange = useCallback((nextIndex: number) => {
		activeIndexRef.current = nextIndex;
		setDotIndex(nextIndex);

		if (commitIndexTimerRef.current) clearTimeout(commitIndexTimerRef.current);
		commitIndexTimerRef.current = setTimeout(() => setActiveIndex(nextIndex), SETTLE_MS);
	}, []);

	useEffect(
		() => () => {
			if (commitIndexTimerRef.current) clearTimeout(commitIndexTimerRef.current);
		},
		[],
	);

	const handleDotPress = useCallback((index: number) => {
		setDotIndex(index);
		carouselRef.current?.scrollToIndex(index);
	}, []);

	const renderTables = () => {
		if (loading) {
			return (
				<View style={styles.centeredFill}>
					<ActivityIndicator size='large' color='#B7C1D1' />
				</View>
			);
		}

		if (tablesLocal.length === 0) {
			return (
				<View style={styles.centeredFill}>
					<Text style={styles.emptyText}>
						{t('T_SITE_LOBBY_NO_TABLES', { defaultValue: 'No tables available' })}
					</Text>
				</View>
			);
		}

		return (
			<>
				<RoomTablesCarousel
					key={layoutKey}
					ref={carouselRef}
					tables={tablesLocal}
					roomSlug={params?.roomSlug}
					activeIndex={activeIndex}
					onActiveIndexChange={handleActiveIndexChange}
					width={width}
					height={sectionHeight}
					cardWidth={cardWidth}
					cardHeight={cardHeight}
					progress={carouselProgress}
				/>

				<View style={[styles.nav, { marginTop: navOffset }]}>
					<CarouselNav
						count={tablesLocal.length}
						activeIndex={dotIndex}
						progress={carouselProgress}
						onDotPress={handleDotPress}
						scale={navScale}
					/>
				</View>
			</>
		);
	};

	return (
		<>
			{isDesktop ? (
				// The desktop frame lays the tables out flat instead of on the swipe wheel.
				<DesktopStripPage
					items={tablesLocal}
					keyOf={(table, index) => String(table.id ?? index)}
					cardAspect={1 / CARD_ASPECT}
					loading={loading}
					initialIndex={Math.floor((tablesLocal.length - 1) / 2)}
					emptyLabel={t('T_SITE_LOBBY_NO_TABLES', { defaultValue: 'No tables available' })}
					hintLabel='Explore tables'
					renderItem={(table, index, size) => (
						<TableCard
							table={table}
							roomSlug={params?.roomSlug}
							index={index}
							totalCount={tablesLocal.length}
							cardWidth={size.width}
							cardHeight={size.height}
							framed
						/>
					)}
				/>
			) : (
				<View
					pointerEvents='box-none'
					style={[styles.container, { width, height: availableHeight, maxHeight: availableHeight }]}
				>
					{renderTables()}
				</View>
			)}

			{showLeveledUpNotification && newLevel && (
				<LeveledUpNotificationModal
					visible={showLeveledUpNotification}
					level={newLevel}
					onClose={() => setShowLeveledUpNotification(false)}
				/>
			)}
		</>
	);
};

const styles = StyleSheet.create({
	container: {
		position: 'relative',
		alignSelf: 'center',
		overflow: 'visible',
	},
	centeredFill: {
		flex: 1,
		alignItems: 'center',
		justifyContent: 'center',
	},
	nav: {
		alignSelf: 'center',
		zIndex: 5,
	},
	emptyText: {
		color: '#fff',
		fontWeight: '700',
	},
});

export default RoomTablesSection;
